import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, FileDown, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { canAny } from '../../api/client';
import {
  fetchInventory,
  fetchInventoryTree,
  generateReport,
  reportFileUrl,
  updateInventory,
  waitForReport,
} from '../../api/ops';
import { usePrefs } from '../../app/prefs';
import { Card, Field, Kpi, Modal, PageHeader, PresenceBadge, ProtocolTag } from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';

/**
 * Equipment inventory (superadmin / installer): per client → locality → zone, the installed equipment with type, model,
 * identifiers, firmware, GPS, state, commissioning date, nominal power (declared or estimated, with its evidence) and
 * relations (concentrator, pole, cabinet). Installation sheet (PDF) and inventory (XLSX/CSV) per location.
 */
export function InventoryPage() {
  const { t } = useTranslation();
  const tree = useQuery({ queryKey: ['inventory', 'tree'], queryFn: fetchInventoryTree });
  const [siteId, setSiteId] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const nodes = useMemo(() => tree.data?.items ?? [], [tree.data]);
  const children = useMemo(() => {
    const m = new Map<string | null, api.InventoryTree['items']>();
    const ids = new Set(nodes.map((n) => n.id));
    for (const n of nodes) {
      const parent = n.parent_id && ids.has(n.parent_id) ? n.parent_id : null;
      m.set(parent, [...(m.get(parent) ?? []), n]);
    }
    return m;
  }, [nodes]);
  const selected = nodes.find((n) => n.id === siteId) ?? null;
  const render = (parent: string | null, depth: number): React.ReactNode =>
    (children.get(parent) ?? []).map((n) => {
      const kids = children.get(n.id) ?? [];
      return (
        <div key={n.id}>
          <button
            type="button"
            aria-current={siteId === n.id}
            style={{ paddingLeft: 6 + depth * 14 }}
            onClick={() => setSiteId(n.id)}
          >
            {kids.length > 0 ? (
              <ChevronRight
                size={14}
                style={{ transform: open.has(n.id) ? 'rotate(90deg)' : undefined, flex: 'none' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const s = new Set(open);
                  if (s.has(n.id)) s.delete(n.id);
                  else s.add(n.id);
                  setOpen(s);
                }}
              />
            ) : (
              <span style={{ width: 14, flex: 'none' }} />
            )}
            <span className="ellipsis" style={{ flex: 1 }}>
              {n.name}
            </span>
            <span className="muted small num">{formatNumber(n.devices_total)}</span>
          </button>
          {open.has(n.id) && render(n.id, depth + 1)}
        </div>
      );
    });
  return (
    <div className="page">
      <PageHeader title={t('inventory.title')} subtitle={t('inventory.subtitle')} />
      <div className="split">
        <Card title={t('inventory.locations')} bodyClass="card-body inv-tree">
          {render(null, 0)}
        </Card>
        {selected ? (
          <SiteInventory node={selected} />
        ) : (
          <Card>
            <p className="muted">{t('inventory.pick')}</p>
          </Card>
        )}
      </div>
    </div>
  );
}

function rootOf(nodes: api.InventoryTree['items'], id: string): string {
  let n = nodes.find((x) => x.id === id);
  while (n?.parent_id && nodes.some((x) => x.id === n!.parent_id))
    n = nodes.find((x) => x.id === n!.parent_id);
  return n?.id ?? id;
}

function SiteInventory({ node }: { node: api.InventoryTree['items'][number] }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const qc = useQueryClient();
  const locale = usePrefs((s) => s.locale);
  const tree = qc.getQueryData<api.InventoryTree>(['inventory', 'tree']);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<api.InventoryItem | null>(null);
  const list = useQuery({
    queryKey: ['inventory', 'list', node.id, q],
    queryFn: () => fetchInventory(node.id, q || undefined),
  });
  const [doc, setDoc] = useState<api.Report | null>(null);
  const gen = useMutation({
    mutationFn: async (type: 'installation_sheet' | 'inventory') => {
      setDoc(null);
      const clientId = rootOf(tree?.items ?? [], node.id);
      const r = await generateReport({
        type,
        client_id: clientId,
        ...(node.id !== clientId || type === 'installation_sheet' ? { site_id: node.id } : {}),
        locale,
      });
      return waitForReport(r.id);
    },
    onSuccess: setDoc,
  });
  const totals = list.data?.totals;
  return (
    <div className="ops-stack">
      <Card
        title={node.name}
        actions={
          <span className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={gen.isPending}
              onClick={() => gen.mutate('installation_sheet')}
            >
              <FileText size={15} /> {t('inventory.sheet')}
            </button>
            <button
              type="button"
              className="btn"
              disabled={gen.isPending}
              onClick={() => gen.mutate('inventory')}
            >
              <FileDown size={15} /> {t('inventory.export')}
            </button>
          </span>
        }
      >
        {doc?.status === 'ready' && (
          <p className="toolbar">
            <span className="form-ok">{t(`reportsPage.types.${doc.type}`)}:</span>
            {doc.formats.map((f) => (
              <a key={f} className="btn" href={reportFileUrl(doc.id, f)} download>
                <FileDown size={14} /> {f.toUpperCase()}
              </a>
            ))}
          </p>
        )}
        {gen.isPending && <p className="muted">{t('reportsPage.generating')}</p>}
        {totals && (
          <div className="kpi-row">
            <Kpi label={t('inventory.devices')} value={formatNumber(totals.devices)} />
            <Kpi
              label={t('inventory.nominalKnown')}
              value={`${formatNumber(totals.nominal_known)} / ${formatNumber(totals.devices)}`}
            />
            <Kpi
              label={t('inventory.installedPower')}
              value={`${formatNumber(totals.nominal_total_w / 1000, 2)} kW`}
            />
            <Kpi label={t('inventory.assets')} value={formatNumber(node.assets_total)} />
          </div>
        )}
        {totals && (
          <p className="muted small">
            {Object.entries(totals.by_protocol)
              .map(([k, n]) => `${k}: ${formatNumber(n)}`)
              .join(' · ')}
            {node.projects.length > 0 &&
              ` · ${t('inventory.projects')}: ${node.projects.map((p) => p.name).join(', ')}`}
          </p>
        )}
        <p className="muted small" style={{ marginBottom: 0 }}>
          {t('inventory.nominalHint')}
        </p>
      </Card>
      <Card
        bodyClass=""
        title={t('inventory.equipment')}
        actions={
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('inventory.search')}
            aria-label={t('common.search')}
          />
        }
      >
        <div className="ops-scroll" style={{ maxHeight: '70vh' }}>
          <table className="table table-tight table-wrap">
            <thead>
              <tr>
                <th>{t('inventory.item')}</th>
                <th>{t('inventory.identifiers')}</th>
                <th>{t('inventory.gps')}</th>
                <th>{t('inventory.nominal')}</th>
                <th>{t('inventory.commissioned')}</th>
                <th>{t('inventory.relations')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).slice(0, 3000).map((d) => (
                <tr
                  key={d.id}
                  className={canAny(me, 'inventory.manage') ? 'row-click' : undefined}
                  onClick={() => canAny(me, 'inventory.manage') && setEditing(d)}
                >
                  <td>
                    <Link to={`/devices/${d.id}`} onClick={(e) => e.stopPropagation()}>
                      {d.name}
                    </Link>
                    <span className="cell-sub">
                      {d.site_name} ·{' '}
                      {d.model ??
                        t(`catalog.deviceType.${d.device_type}` as 'catalog.deviceType.other', {
                          defaultValue: d.device_type,
                        })}
                    </span>
                    <ProtocolTag protocol={d.protocol} />
                  </td>
                  <td className="mono small">
                    {d.identifiers.map((i) => (
                      <span key={i.scheme} style={{ display: 'block' }}>
                        <span className="muted">{i.scheme}</span> {i.value}
                      </span>
                    ))}
                    {d.firmware && (
                      <span style={{ display: 'block' }}>
                        <span className="muted">fw</span> {d.firmware}
                      </span>
                    )}
                  </td>
                  <td className="mono small nowrap">
                    {d.lat !== null ? `${d.lat.toFixed(6)}, ${d.lon?.toFixed(6)}` : '—'}
                    {d.location_source && (
                      <span className="cell-sub">
                        {t(
                          `inventory.locationSource.${d.location_source}` as 'inventory.locationSource.manual',
                          { defaultValue: d.location_source },
                        )}
                      </span>
                    )}
                  </td>
                  <td>
                    <NominalCell n={d.nominal} />
                  </td>
                  <td className="nowrap">
                    {d.commissioned_on ??
                      (d.first_seen_at ? formatDateTime(d.first_seen_at).slice(0, 10) : '—')}
                    <span className="cell-sub">
                      {d.commissioned_on
                        ? t('inventory.entered')
                        : d.first_seen_source
                          ? t(
                              `inventory.firstSeen.${d.first_seen_source}` as 'inventory.firstSeen.connection',
                              { defaultValue: d.first_seen_source },
                            )
                          : ''}
                    </span>
                  </td>
                  <td className="small">
                    {d.upstream && (
                      <span style={{ display: 'block' }}>
                        {t('inventory.via')} <Link to={`/devices/${d.upstream.id}`}>{d.upstream.name}</Link>
                      </span>
                    )}
                    {d.downstream_count > 0 && (
                      <span style={{ display: 'block' }}>
                        {t('inventory.downstream', { n: d.downstream_count })}
                      </span>
                    )}
                    {d.relations.map((r) => (
                      <span key={`${r.kind}-${r.id}`} style={{ display: 'block' }}>
                        {t(`assetsPage.relation.${r.kind}` as 'assetsPage.relation.mounted_on')}
                        {r.direction === 'in' ? ' ←' : ' →'} {r.name}
                      </span>
                    ))}
                    {d.group && <span className="pill">{d.group}</span>}
                  </td>
                  <td>
                    <PresenceBadge presence={d.presence as 'online'} />
                    {d.active_alarms > 0 && (
                      <span className="pill pill-warning">
                        {t('inventory.alarms', { n: d.active_alarms })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {editing && <FactsModal item={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function NominalCell({ n }: { n: api.NominalPower }) {
  const { t } = useTranslation();
  const tone =
    n.source === 'manual' || n.source === 'legacy'
      ? 'pill-online'
      : n.source === 'estimated'
        ? 'pill-info'
        : 'pill-warning';
  const evidence = [
    n.measured_w !== null ? t('inventory.measured', { w: formatNumber(n.measured_w, 1) }) : null,
    n.samples !== null ? t('inventory.samples', { n: n.samples }) : null,
    n.ratio !== null ? `ratio ${n.ratio}` : null,
    n.confidence ? t(`inventory.confidence.${n.confidence}` as 'inventory.confidence.high') : null,
    n.reason,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <span title={evidence}>
      <strong>{n.value_w !== null ? `${formatNumber(n.value_w)} W` : '—'}</strong>{' '}
      <span className={`pill ${tone}`}>{t(`inventory.nominalSource.${n.source}`)}</span>
      {n.mismatch && (
        <span className="cell-sub" style={{ color: 'var(--color-warning)' }}>
          {t('inventory.mismatch', { w: formatNumber(n.estimated_w ?? undefined) })}
        </span>
      )}
      {n.measured_w !== null && (
        <span className="cell-sub">{t('inventory.measured', { w: formatNumber(n.measured_w, 1) })}</span>
      )}
    </span>
  );
}

function FactsModal({ item, onClose }: { item: api.InventoryItem; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [commissioned, setCommissioned] = useState(item.commissioned_on ?? '');
  const [power, setPower] = useState(
    item.nominal.source === 'manual' || item.nominal.source === 'legacy'
      ? String(item.nominal.value_w ?? '')
      : '',
  );
  const [address, setAddress] = useState(item.address ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateInventory(item.id, {
        commissioned_on: commissioned || null,
        nominal_power_w: power ? Number(power.replace(',', '.')) : null,
        address: address || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] });
      onClose();
    },
  });
  return (
    <Modal title={item.name} subtitle={t('inventory.facts')} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('inventory.factsHint')}
      </p>
      <div className="form-grid">
        <Field
          label={t('inventory.commissioned')}
          hint={
            item.first_seen_at
              ? t('inventory.firstSeenAt', { at: formatDateTime(item.first_seen_at) })
              : undefined
          }
        >
          <input
            className="input"
            type="date"
            value={commissioned}
            onChange={(e) => setCommissioned(e.target.value)}
          />
        </Field>
        <Field
          label={t('inventory.nominalDeclared')}
          hint={
            item.nominal.estimated_w !== null
              ? t('inventory.estimateIs', {
                  w: formatNumber(item.nominal.estimated_w),
                  status: t(`inventory.nominalSource.${item.nominal.source}`),
                })
              : undefined
          }
        >
          <input
            className="input"
            inputMode="decimal"
            value={power}
            onChange={(e) => setPower(e.target.value)}
            placeholder="W"
          />
        </Field>
        <Field label={t('inventory.address')}>
          <input
            className="input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            maxLength={300}
          />
        </Field>
      </div>
      {save.isError && <p className="form-error">{t('errors.INVALID_REQUEST')}</p>}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}
